import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import { useAggregatePlatformsForWidgetQuery } from '../../graphql/search.generated';
import { DonutChart } from '../shared/charts';
import { DonutDataItem } from '../shared/charts/chartConfigUtilService';
import { formatNumber } from '../shared/formatNumber';
import { useTranslation } from '../../i18n/useTranslation';
import { PLATFORM_FILTER_NAME } from '../search/utils/constants';
import {
    appendOtherSlice,
    COMMON_ASSET_ENTITY_TYPES,
    getAggDisplayName,
    getFacetAggregations,
    WIDGET_CHART_COLORS,
    WIDGET_TOP_ITEMS_LIMIT,
} from './utils';
import { ChartWidgetCard } from './ChartWidgetCard';
import { navigateToSearchUrl } from '../search/utils/navigateToSearchUrl';
import { mergeDashboardFilters, useDashboardFilters } from './dashboardFilters';

export const PlatformsWidget = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const dashboardFilters = useDashboardFilters();

    const {
        data: platformsData,
        previousData: previousPlatformsData,
        loading: platformsLoading,
    } = useAggregatePlatformsForWidgetQuery({
        variables: {
            input: {
                query: '*',
                facets: [PLATFORM_FILTER_NAME],
                types: COMMON_ASSET_ENTITY_TYPES,
                searchFlags: { maxAggValues: 100 },
                orFilters: mergeDashboardFilters(dashboardFilters),
            },
        },
        fetchPolicy: 'no-cache',
    });

    const effectiveData = platformsData ?? previousPlatformsData;

    const platformAggregations = useMemo(
        () => getFacetAggregations(effectiveData?.aggregateAcrossEntities?.facets, PLATFORM_FILTER_NAME),
        [effectiveData],
    );

    const platformsChartData = useMemo(() => {
        if (platformAggregations.length === 0) {
            return [];
        }

        const sortedPlatforms = [...platformAggregations].sort((a, b) => b.count - a.count);
        const topPlatforms = sortedPlatforms.slice(0, WIDGET_TOP_ITEMS_LIMIT);
        const remainingPlatforms = sortedPlatforms.slice(WIDGET_TOP_ITEMS_LIMIT);

        const chartData: DonutDataItem[] = topPlatforms.map((agg, index) => {
            const platformEntity = agg.entity?.__typename === 'DataPlatform' ? agg.entity : null;
            const name =
                platformEntity?.properties?.displayName ||
                platformEntity?.displayName ||
                platformEntity?.name ||
                getAggDisplayName(agg, t('Unknown'));

            return {
                name,
                y: agg.count,
                color: WIDGET_CHART_COLORS[index % WIDGET_CHART_COLORS.length],
                urn: agg.value,
            };
        });

        const otherTotal = remainingPlatforms.reduce((sum, agg) => sum + agg.count, 0);
        return appendOtherSlice(chartData, otherTotal, t('Other'));
    }, [platformAggregations, t]);

    const totalPlatformsCount = platformAggregations.length;

    // Stable reference so DonutChart's memoized config doesn't change every render.
    const handlePointClick = useCallback(
        (_name: string, urn?: string) => {
            if (urn) {
                navigateToSearchUrl({
                    filters: [{ field: PLATFORM_FILTER_NAME, values: [urn] }],
                    history,
                });
            }
        },
        [history],
    );

    return (
        <ChartWidgetCard title={t('Platforms')} loading={platformsLoading} isEmpty={platformsChartData.length === 0}>
            <DonutChart
                height={310}
                seriesName={t('Entities')}
                data={platformsChartData}
                subText={t('Total Platforms')}
                centerText={String(formatNumber(totalPlatformsCount))}
                onPointClick={handlePointClick}
            />
        </ChartWidgetCard>
    );
};
